import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * The signed-in user id, so auth events that don't change identity can be
   * ignored.
   *
   * Supabase hands back a NEW session object on every token refresh and on tab
   * re-focus. Calling setSession with it changes the reference, which re-runs
   * every effect keyed on `[session]` across the app — refetching data and
   * discarding whatever was in progress. That will matter a great deal once the
   * planning board holds unsaved edits. Learned from ardent-scholar, which hit
   * exactly this and lost in-flight work to it.
   */
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => {
        currentUserIdRef.current = data.session?.user.id ?? null;
        setSession(data.session);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      const nextUserId = next?.user.id ?? null;
      // Same user → token refresh or re-focus, not an identity change. Skip it.
      if (nextUserId === currentUserIdRef.current) return;
      currentUserIdRef.current = nextUserId;
      setSession(next);
    });

    // A tab left open past token expiry comes back with a stale session;
    // asking for it on re-focus triggers the refresh.
    function onVisible() {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().catch(() => {});
      }
    }
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      sub.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ session, loading, signOut }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
